/**
 * CampaignRankingUserRemapper
 *
 * Bridges raw Firestore JSON → CampaignRankingUserDto → CampaignRankingUserEntity.
 * Flutter source: lib/data/models/campaign_ranking_user.dart (+ .g.dart).
 *
 * Two raw entry points mirror Flutter's dual factories, because the live `users`
 * doc and the frozen `ranking` subcollection doc differ:
 *  - `fromUserJson`    ↔ Dart `CampaignRankingUser.fromUser` (campaign_ranking_user.dart:18):
 *                        reads `username` (lowercase), has no `winner` → defaults to `false`.
 *  - `fromRankingJson` ↔ Dart `CampaignRankingUser.fromJson` (.g.dart:9):
 *                        reads `userName` and `winner` (default `false`).
 * Both read `firebase_id` for `firebaseId`.
 */
import type { CampaignRankingUserDto } from '@/data/dtos/campaign/campaignRankingUserDto';
import type { JsonObject } from '@/data/types/json';
import type { CampaignRankingUserEntity } from '@/domain/entities/campaign/campaignRankingUserEntity';

export class CampaignRankingUserRemapper {
  /**
   * Live `users` doc shape (Dart `CampaignRankingUser.fromUser`).
   * Reads `username` (lowercase), no `winner` field → `winner = false`.
   */
  static fromUserJson(data: JsonObject): CampaignRankingUserDto {
    return {
      firebase_id: (data['firebase_id'] as string | undefined) ?? '',
      subscribers: (data['subscribers'] as number | undefined) ?? 0,
      contentCount: (data['contentCount'] as number | undefined) ?? 0,
      likes: (data['likes'] as number | undefined) ?? 0,
      userName: (data['username'] as string | undefined) ?? '',
      winner: false,
    };
  }

  /**
   * Frozen `ranking` subcollection doc shape (Dart `CampaignRankingUser.fromJson`).
   * Reads `userName` and `winner` (default `false`).
   */
  static fromRankingJson(data: JsonObject): CampaignRankingUserDto {
    return {
      firebase_id: (data['firebase_id'] as string | undefined) ?? '',
      subscribers: (data['subscribers'] as number | undefined) ?? 0,
      contentCount: (data['contentCount'] as number | undefined) ?? 0,
      likes: (data['likes'] as number | undefined) ?? 0,
      userName: (data['userName'] as string | undefined) ?? '',
      winner: (data['winner'] as boolean | undefined) ?? false,
    };
  }

  static fromUserJsonList(dataList: JsonObject[]): CampaignRankingUserDto[] {
    return dataList.map(CampaignRankingUserRemapper.fromUserJson);
  }

  static fromRankingJsonList(dataList: JsonObject[]): CampaignRankingUserDto[] {
    return dataList.map(CampaignRankingUserRemapper.fromRankingJson);
  }

  static fromDto(dto: CampaignRankingUserDto): CampaignRankingUserEntity {
    return {
      firebaseId: dto.firebase_id,
      subscribers: dto.subscribers,
      contentCount: dto.contentCount,
      likes: dto.likes,
      userName: dto.userName,
      winner: dto.winner,
    };
  }
}
